import React, { useMemo, useState } from 'react';
import { Button, Card, Collapse, Input, Space, Tag, Typography } from 'antd';
import {
  BookOutlined,
  CarOutlined,
  CreditCardOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  NotificationOutlined,
  SettingOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  ShopOutlined,
  TeamOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import MainLayout from '../../components/MainLayout';

const { Title, Text, Paragraph } = Typography;

const FAQ = [
  {
    key: 'getting-started',
    icon: <BookOutlined style={{ color: '#1677ff' }} />,
    title: 'Getting started',
    items: [
      {
        q: 'How to login?',
        a: 'Use your admin credentials. If you are a Super Admin you will land on the Super Dashboard, otherwise on the Admin Dashboard.',
      },
      {
        q: 'I can’t see some modules in sidebar.',
        a: 'Menu items are role-based. Super Admin and Admin have different access. If you need access, ask the system admin to update your role/permissions.',
      },
      {
        q: 'What is store-scoped data?',
        a: 'Drivers and Warehouses are store-scoped. Admin users can only see/manage data for their store. Super Admin can view multiple stores and optionally filter by store.',
      },
      {
        q: 'What to do after first login?',
        a: 'Recommended steps: 1) Add Warehouse, 2) Add Drivers, 3) Create/Sync Orders, 4) Assign Driver + Warehouse, 5) Track in Live Tracking.',
      },
    ],
  },
  {
    key: 'orders',
    icon: <ShopOutlined style={{ color: '#52c41a' }} />,
    title: 'Orders',
    items: [
      {
        q: 'What is “Pending Order” tab?',
        a: 'It shows orders that are in PENDING status (not yet processed/assigned). This is the default tab on Orders page.',
      },
      {
        q: 'How to create new order?',
        a: 'Orders usually come via platform/API. If you use API docs, follow “API Docs” page to create orders with API key + signature. After creation it will appear in Pending Order tab.',
      },
      {
        q: 'How to assign driver + warehouse?',
        a: 'Step-by-step: 1) Open Orders → Pending Order, 2) Click Assign icon in Driver column, 3) Select Warehouse (auto-selected if only one), 4) Select Driver, 5) Select Payment type, 6) Click “Assign driver now” or “Create shipment”.',
      },
      {
        q: 'How to change payment type?',
        a: 'Open the “Assign Driver to Order” modal and choose Payment from the dropdown, then proceed with Create shipment / Assign driver.',
      },
      {
        q: 'Why warehouse dropdown is disabled sometimes?',
        a: 'If your store has only 1 warehouse, it is auto-selected and the dropdown is disabled to avoid mistakes.',
      },
      {
        q: 'Order details page not opening.',
        a: 'Try clicking the order row again or open via Order Details icon (if available). If it is store access related, Super Admin must view the correct store.',
      },
    ],
  },
  {
    key: 'drivers',
    icon: <TeamOutlined style={{ color: '#722ed1' }} />,
    title: 'Drivers',
    items: [
      {
        q: 'How to create driver (with documents)?',
        a: 'Go to Drivers → Add Driver. Fill basic details and select documents (Aadhar/PAN/License etc). Files are selected locally first; on Submit they upload and save with the driver.',
      },
      {
        q: 'Driver documents show red filename while uploading.',
        a: 'Select files first (no auto-upload). When you submit the form, documents upload with the driver. If it stays red, check network / backend upload logs.',
      },
      {
        q: 'How to set driver online/offline?',
        a: 'Drivers can toggle availability from the mobile app. Admin panel shows current availability in driver listings/details.',
      },
      {
        q: 'Why driver is not visible in Assign modal?',
        a: 'Only active drivers for your store are listed. Also make sure the driver account is created and not blocked.',
      },
    ],
  },
  {
    key: 'warehouse',
    icon: <EnvironmentOutlined style={{ color: '#faad14' }} />,
    title: 'Warehouse',
    items: [
      {
        q: 'How to add warehouse?',
        a: 'Go to Warehouse → Add Warehouse. Fill name, address and choose location (map). Save. Warehouses will be linked to your store automatically.',
      },
      {
        q: 'Warehouses are not showing.',
        a: 'Warehouses are store-scoped. You will only see warehouses created for your store. Super Admin can filter by store when needed.',
      },
      {
        q: 'Warehouse location not correct on map.',
        a: 'You can click on map to set location. Also check latitude/longitude fields if provided.',
      },
    ],
  },
  {
    key: 'live-tracking',
    icon: <CarOutlined style={{ color: '#13c2c2' }} />,
    title: 'Live Tracking',
    items: [
      {
        q: 'How to use Live Tracking?',
        a: 'Open Live Tracking. Select a driver from the right panel to focus the map. You can open Order Details from the eye icon.',
      },
      {
        q: 'Order details sometimes doesn’t load.',
        a: 'If it fails, click refresh in Order Details modal. Also confirm you have access to that order’s store.',
      },
      {
        q: 'How to see distance to customer?',
        a: 'Select a driver from the right panel; the map shows a route to the customer and distance in km.',
      },
      {
        q: 'Route is not showing for some orders.',
        a: 'Route needs both driver GPS and customer delivery latitude/longitude. If address coordinates are missing, only driver marker will show.',
      },
    ],
  },
  {
    key: 'notifications',
    icon: <NotificationOutlined style={{ color: '#1677ff' }} />,
    title: 'Notifications',
    items: [
      {
        q: 'How to send custom notification?',
        a: 'Go to Notifications → create and send. Use it to inform drivers/admin users about important updates.',
      },
    ],
  },
  {
    key: 'plans',
    icon: <SettingOutlined style={{ color: '#52c41a' }} />,
    title: 'Plans',
    items: [
      {
        q: 'What is Plans page?',
        a: 'Plans are used to manage subscription/features. Super Admin can create/edit plans. Admin can select a plan (if required).',
      },
    ],
  },
  {
    key: 'api-docs',
    icon: <FileTextOutlined style={{ color: '#722ed1' }} />,
    title: 'API Docs',
    items: [
      {
        q: 'Where to find order creation API documentation?',
        a: 'Open “API Docs” in sidebar. It includes signature generation and examples in JS/Python/PHP/cURL.',
      },
      {
        q: 'Why signature fails (Invalid signature)?',
        a: 'Common causes: key sorting mismatch, Unicode encoding differences, timestamp format issues. Use the troubleshooting section inside API Docs page.',
      },
    ],
  },
  {
    key: 'payments',
    icon: <CreditCardOutlined style={{ color: '#eb2f96' }} />,
    title: 'Payments / COD',
    items: [
      {
        q: 'Today driver-wise COD vs Direct analytics.',
        a: 'Open Dashboard → Today: Driver-wise COD vs Direct section for totals and per-driver breakdown.',
      },
      {
        q: 'How to confirm COD vs Prepaid in order?',
        a: 'Open order details and check Payment Type tag. You can update it from the Assign Driver modal before creating shipment/assigning driver.',
      },
    ],
  },
  {
    key: 'troubleshooting',
    icon: <WarningOutlined style={{ color: '#ff4d4f' }} />,
    title: 'Troubleshooting',
    items: [
      {
        q: 'Page changes are not visible.',
        a: 'Hard refresh the browser. If running dev server, confirm you are on the correct port and the build is up to date.',
      },
      {
        q: 'Map is blank / drivers disappear.',
        a: 'Confirm driver has GPS updates and the shipment is in an active status. Network issues can temporarily hide live data.',
      },
      {
        q: 'Upload fails / documents not saving.',
        a: 'Check backend server logs for upload errors, and confirm temp directory exists. Also ensure you are not exceeding file size limits.',
      },
    ],
  },
];

export default function UserGuide() {
  const [q, setQ] = useState('');

  const normalized = q.trim().toLowerCase();
  const panels = useMemo(() => {
    const match = (s) => String(s || '').toLowerCase().includes(normalized);
    const filtered = FAQ
      .map((section) => {
        const items = (section.items || []).filter((it) => {
          if (!normalized) return true;
          return match(it.q) || match(it.a) || match(section.title);
        });
        return { ...section, items };
      })
      .filter((s) => (s.items || []).length > 0);

    return filtered.map((section) => ({
      key: section.key,
      label: (
        <Space size={8}>
          {section.icon}
          <span style={{ fontWeight: 700 }}>{section.title}</span>
          {normalized && <Tag color="blue">filtered</Tag>}
        </Space>
      ),
      children: (
        <div style={{ display: 'grid', gap: 10 }}>
          {section.items.map((it, idx) => (
            <div
              key={`${section.key}-${idx}`}
              style={{
                padding: 12,
                border: '1px solid #f0f0f0',
                borderRadius: 12,
                background: '#fff',
              }}
            >
              <Text strong style={{ display: 'block', marginBottom: 6, color: '#1e293b' }}>
                <QuestionCircleOutlined style={{ marginRight: 6, color: '#1677ff' }} />
                {it.q}
              </Text>
              <Paragraph style={{ margin: 0, color: '#475569' }}>{it.a}</Paragraph>
            </div>
          ))}
        </div>
      ),
    }));
  }, [normalized]);

  return (
    <MainLayout>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ marginBottom: 12 }}>
          <Space align="center" size={10} wrap>
            <BookOutlined style={{ fontSize: 22, color: '#1677ff' }} />
            <Title level={3} style={{ margin: 0 }}>User Guide / FAQ</Title>
            <Tag color="geekblue">Admin Panel</Tag>
          </Space>
          <Text type="secondary" style={{ display: 'block', marginTop: 6 }}>
            Search anything (Orders, Drivers, Warehouse, Live Tracking, Payments) and expand to see steps.
          </Text>
        </div>

        <Card style={{ borderRadius: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              allowClear
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              placeholder="Search in FAQ…"
              style={{ flex: 1, minWidth: 260 }}
            />
            <Button
              onClick={() => setQ('')}
              disabled={!q}
            >
              Clear
            </Button>
          </div>

          <Collapse
            accordion={false}
            items={panels}
            defaultActiveKey={['getting-started']}
            style={{ marginTop: 12 }}
          />
        </Card>
      </div>
    </MainLayout>
  );
}

